import { useMemo, useState } from 'react'
import { FileSpreadsheet, TriangleAlert } from 'lucide-react'
import Button from './Button'
import DataTable from './DataTable'
import FilterBar from './FilterBar'
import Modal from './Modal'
import { InfoNote } from './Form'
import { useToast } from './toastContext'
import { kit } from './strings'
import { safeFileName, saveWorkbook } from '../lib/spreadsheet'
import './RejectedRowsModal.css'

/**
 * The entries a bulk add could not take.
 *
 * Two endpoints answer one with a list of what they rejected, and both used to arrive as a
 * toast: five names and a `+12` from `AddUsersToGroup`, a paragraph of names run together
 * from `CreateMultiUsers`. Neither is usable for the one thing an administrator does next —
 * work out which lines of their spreadsheet are wrong. So it is a list: searchable, and
 * exportable as the file they will correct and re-import.
 *
 * NOT A FAILURE DIALOG. Both endpoints save the rows they could read BEFORE reporting the
 * rest, so there is nothing to retry and the only action is Close. The caller is what
 * refreshes the list behind it.
 *
 * IT WRITES THE FILE ITSELF, rather than taking an `onExport`. A service module exists to
 * own a URL and there is no URL here: the server never sent this report as a file and has no
 * endpoint that would. `lib/spreadsheet` is the shared adapter for composing one, so the
 * export is a detail of this control instead of ten lines each caller repeats.
 *
 * @param {{ names: string[], subject?: string, message?: string } | null} report
 *        a SNAPSHOT taken when the response arrived, so the sheet it came from can close
 *        underneath without emptying the list. `subject` names what was being added to.
 * @param {object} labels  the caller's own strings — `title`, `lead`, `hint`, `search`,
 *        `count(shown, total)`, `empty`, `indexHeader`, `nameHeader`, `fileName(subject)`,
 *        `sheetName`, `exportFailed`. The Close and Export wording comes from the kit: it is
 *        the same control on both screens and reads the same on each.
 */
export default function RejectedRowsModal({ report, labels, onClose }) {
  const toast = useToast()
  const [search, setSearch] = useState('')
  const [busy, setBusy] = useState(false)

  /* Numbered ON THE ROW rather than at render time, so sorting by name does not renumber
     them — the number is the entry's position in the report, which is what makes it
     findable in the file it came from. */
  const all = useMemo(
    () =>
      (report?.names ?? []).map((value, index) => ({
        id: `${index}-${value}`,
        order: index + 1,
        value,
      })),
    [report],
  )

  /* THE FILTER IS OWNED HERE, not handed to DataTable, because the export has to ship
     exactly what is on screen and a needle inside the table cannot be read back out. */
  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return all
    return all.filter((row) => row.value.toLowerCase().includes(needle))
  }, [all, search])

  const columns = useMemo(
    () => [
      {
        key: 'order',
        header: labels.indexHeader,
        width: '4rem',
        searchable: false,
        render: (row) => <span className="rejected-rows__index">{row.order}</span>,
      },
      {
        key: 'value',
        header: labels.nameHeader,
        render: (row) => <bdi>{row.value}</bdi>,
      },
    ],
    [labels],
  )

  const runExport = async () => {
    setBusy(true)
    try {
      await saveWorkbook([[labels.nameHeader], ...rows.map((row) => [row.value])], {
        fileName: safeFileName(labels.fileName(report?.subject ?? ''), { fallback: 'report' }),
        sheetName: labels.sheetName,
        columnWidths: [34],
      })
    } catch {
      /* Covering this modal is correct: the dialog is ABOUT the export that just failed, and
         there is nothing to read underneath it until it is dismissed. The thrown message is
         the parser's own, in English, so the screen's wording is used instead. */
      toast.error(labels.exportFailed)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={Boolean(report)}
      size="lg"
      icon={TriangleAlert}
      title={labels.title}
      subtitle={report?.subject}
      onClose={onClose}
      footer={
        <Button variant="ghost" onClick={onClose}>
          {kit.close}
        </Button>
      }
    >
      <div className="rejected-rows">
        <InfoNote icon={TriangleAlert} lead={labels.lead}>
          <bdi>{report?.message || labels.hint}</bdi>
        </InfoNote>

        <FilterBar
          search={{ value: search, onChange: setSearch, placeholder: labels.search }}
          count={labels.count(rows.length, all.length)}
          actions={
            <Button variant="soft" busy={busy} disabled={rows.length === 0} onClick={runExport}>
              <FileSpreadsheet size={16} strokeWidth={1.75} />
              {kit.export}
            </Button>
          }
        />

        <DataTable
          columns={columns}
          rows={rows}
          /* The FilterBar above is the search box; the table must not offer a second one. */
          searchable={false}
          emptyMessage={labels.empty}
          /* The table cannot see the filter above it, so searching from page 3 of a result
             that is now one page long would leave the pager there. */
          resetKey={`${search}|${all.length}`}
          /* A report is read once and thrown away: it neither remembers its position nor
             writes `?page=` into the address. */
          stateKey={false}
        />
      </div>
    </Modal>
  )
}
